import { createFileRoute, Link } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import { AlertCircle, CheckCircle2, Database, Search, Workflow } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getAdminRealtimeJobDiagnosticServer,
  type AdminRealtimeJobDiagnosticResponse,
  type AdminRealtimeStatusCount,
} from "@/server/admin";

interface RealtimeJobsSearchParams {
  jobId?: string;
}

export const Route = createFileRoute("/admin/realtime-jobs")({
  validateSearch: (search: Record<string, unknown>): RealtimeJobsSearchParams => ({
    jobId: typeof search.jobId === "string" ? search.jobId : undefined,
  }),
  loaderDeps: ({ search }) => ({
    jobId: search.jobId,
  }),
  loader: async ({ deps }) => {
    const jobId = deps.jobId?.trim();
    if (!jobId) {
      return { diagnostic: null };
    }
    const diagnostic = await getAdminRealtimeJobDiagnosticServer({ data: { jobId } });
    return { diagnostic };
  },
  component: AdminRealtimeJobsPage,
});

function AdminRealtimeJobsPage() {
  const { diagnostic } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [jobIdInput, setJobIdInput] = useState(search.jobId ?? "");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    navigate({
      search: {
        jobId: jobIdInput.trim() || undefined,
      },
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b px-6 py-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">Realtime Job Diagnostics</h2>
          <p className="text-sm text-muted-foreground">
            Inspect webhook consumption, query completion, target company counts, and deal output by
            realtime job ID.
          </p>
        </div>
        <form className="mt-4 flex max-w-3xl gap-2" onSubmit={handleSubmit}>
          <Input
            value={jobIdInput}
            onChange={(event) => setJobIdInput(event.target.value)}
            placeholder="Enter realtime job_id"
            className="font-mono text-sm"
          />
          <Button type="submit">
            <Search className="h-4 w-4" />
            Inspect
          </Button>
        </form>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {!diagnostic ? <EmptyState /> : <DiagnosticContent diagnostic={diagnostic} />}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex min-h-72 items-center justify-center rounded-lg border border-dashed">
      <div className="max-w-md text-center">
        <Database className="mx-auto h-8 w-8 text-muted-foreground" />
        <h3 className="mt-3 text-sm font-semibold">Enter a job ID to inspect consumption</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The page reads the current environment database and shows persisted webhook state.
        </p>
      </div>
    </div>
  );
}

function DiagnosticContent({ diagnostic }: { diagnostic: AdminRealtimeJobDiagnosticResponse }) {
  if (!diagnostic.found || !diagnostic.job) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-destructive">
          <AlertCircle className="h-4 w-4" />
          Realtime job not found
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{diagnostic.summary}</p>
      </div>
    );
  }

  const latestEvent = diagnostic.events[0];
  const query = diagnostic.query;
  const campaign = diagnostic.campaign;
  const dealSummary = diagnostic.deal_summary;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <StatusBadge status={diagnostic.consumption_status} />
              <span className="font-mono text-sm text-muted-foreground">
                {diagnostic.job.job_id}
              </span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{diagnostic.summary}</p>
          </div>
          {campaign?.workspace_slug ? (
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/workspace/$workspaceSlug/campaigns/$campaignId"
                params={{
                  workspaceSlug: campaign.workspace_slug,
                  campaignId: campaign.campaign_id,
                }}
                search={{ tab: "conversation" }}
                target="_blank"
                rel="noreferrer"
              >
                Open Campaign
              </Link>
            </Button>
          ) : null}
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Payload companies" value={latestEvent?.result_companies_count ?? "-"} />
        <MetricCard label="Job status" value={diagnostic.job.status} />
        <MetricCard label="Query status" value={query?.status ?? "-"} />
        <MetricCard label="Recent deals" value={dealSummary?.recent_deals_after_webhook ?? 0} />
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <InfoPanel
          title="Job"
          rows={[
            ["Owner type", diagnostic.job.owner_type ?? "-"],
            ["Provider", diagnostic.job.provider ?? "-"],
            ["Search query", diagnostic.job.search_query],
            ["Country", diagnostic.job.country_code ?? "-"],
            ["Submitted", formatDateTime(diagnostic.job.submitted_at)],
            ["Last webhook", formatDateTime(diagnostic.job.last_webhook_at)],
            ["Finished", formatDateTime(diagnostic.job.finished_at)],
          ]}
        />
        <InfoPanel
          title="Campaign"
          rows={[
            ["Campaign", campaign?.campaign_name ?? "-"],
            ["Campaign ID", campaign?.campaign_id ?? diagnostic.job.campaign_id],
            ["Campaign status", campaign?.campaign_status ?? "-"],
            ["Workspace", campaign?.workspace_slug ?? diagnostic.job.workspace_id],
            ["ICP ID", diagnostic.job.icp_id ?? "-"],
            ["Realtime query ID", diagnostic.job.realtime_query_id ?? "-"],
          ]}
        />
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <CountsTable title="Campaign target counts" counts={diagnostic.target_counts} />
        <CountsTable title="This ICP target counts" counts={diagnostic.source_icp_target_counts} />
      </section>

      <section className="rounded-lg border">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Workflow className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Webhook events</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Result companies</TableHead>
                <TableHead>Top-level companies</TableHead>
                <TableHead>Processed</TableHead>
                <TableHead>Event key</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {diagnostic.events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No persisted webhook events.
                  </TableCell>
                </TableRow>
              ) : (
                diagnostic.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.status}</TableCell>
                    <TableCell>{event.result_companies_count ?? "-"}</TableCell>
                    <TableCell>{event.top_level_companies_count ?? "-"}</TableCell>
                    <TableCell>{formatDateTime(event.processed_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{event.event_key}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: AdminRealtimeJobDiagnosticResponse["consumption_status"];
}) {
  if (status === "consumed") {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Consumed
      </Badge>
    );
  }
  if (status === "failed") {
    return <Badge variant="destructive">Failed</Badge>;
  }
  if (status === "finished_without_event") {
    return <Badge variant="outline">Finished without event</Badge>;
  }
  return <Badge variant="secondary">Processing</Badge>;
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-2 break-words text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function InfoPanel({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <section className="rounded-lg border">
      <h3 className="border-b px-4 py-3 text-sm font-semibold">{title}</h3>
      <dl className="divide-y">
        {rows.map(([label, value]) => (
          <div key={label} className="grid gap-2 px-4 py-3 sm:grid-cols-[160px_1fr]">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="break-words font-mono text-sm">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CountsTable({ title, counts }: { title: string; counts: AdminRealtimeStatusCount[] }) {
  return (
    <section className="rounded-lg border">
      <h3 className="border-b px-4 py-3 text-sm font-semibold">{title}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Kind</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Count</TableHead>
            <TableHead>Last updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {counts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                No target companies.
              </TableCell>
            </TableRow>
          ) : (
            counts.map((item) => (
              <TableRow key={`${item.candidate_kind}-${item.status}`}>
                <TableCell>{item.candidate_kind}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell className="text-right font-medium">{item.count}</TableCell>
                <TableCell>{formatDateTime(item.last_updated_at)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </section>
  );
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}
